import React from 'react';
import theme from 'resources/themes';
import { Menu as MenuIcon, X } from 'lucide-react';
import { useMobileDrawer } from 'hooks/useSidebar';

/**
 * Sidebar drawer wrapper.
 *  - Desktop (>1080px): sidebar always visible (transform: translateX(0)).
 *  - Mobile (<=1080px): hamburger toggles a drawer + dimmed overlay.
 *
 * MYID-H9 (2026-05-26) refactor:
 *  - Drawer open/close state lifted into SidebarContext (useMobileDrawer)
 *    so the SidebarProvider's route-change effect auto-closes the
 *    drawer when a nav item navigates.
 *  - Hamburger moved to position:fixed (was absolute) so it persists
 *    on scroll AND stays accessible above the header.
 *  - Hamburger sized + chip-styled (40px white pill with subtle
 *    shadow) to clear the 56px header bar and read as a touch target.
 *  - Overlay darkened (rgba black 0.45) for higher contrast.
 *  - Sidebar max-width capped at 85vw so very narrow screens still
 *    show a sliver of the underlying page (helps users find the tap-
 *    outside-to-close affordance).
 *  - role/aria-modal attributes added for screen-reader behavior.
 */
function MenuComponent({ children, isMobile }) {
  const { isMobileOpen, setIsMobileOpen } = useMobileDrawer();

  return (
    <>
      {/* Hamburger button — only on mobile, fixed so it persists on scroll */}
      {isMobile && !isMobileOpen && (
        <button
          onClick={() => setIsMobileOpen(true)}
          aria-label="Open navigation"
          style={{
            position: 'fixed',
            left: 12,
            top: 12,
            zIndex: 40,
            background: '#ffffff',
            border: `1px solid ${theme.color.lightGrayishBlue2 || '#DFE0EB'}`,
            borderRadius: 8,
            width: 40,
            height: 40,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.color.veryDarkGrayishBlue,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <MenuIcon size={22} />
        </button>
      )}

      {/* Overlay — tap to close */}
      {isMobile && isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 30,
            transition: 'opacity 0.2s ease',
          }}
        />
      )}

      {/* Sidebar panel */}
      <div
        role={isMobile ? 'dialog' : undefined}
        aria-modal={isMobile && isMobileOpen ? 'true' : undefined}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: 255,
          maxWidth: '85vw',
          background: theme.color.themeBgColor,
          zIndex: 35,
          overflowY: 'auto',
          transform:
            isMobile && !isMobileOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 0.3s ease-in-out',
          boxShadow:
            isMobile && isMobileOpen ? '2px 0 12px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        {/* Close button — only when drawer is open on mobile */}
        {isMobile && isMobileOpen && (
          <button
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close navigation"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.color.grayishBlue3,
              zIndex: 36,
            }}
          >
            <X size={22} />
          </button>
        )}
        {children}
      </div>
    </>
  );
}

export default MenuComponent;
